import React, { useState } from 'react';
import { Alert, Button, DatePicker, Form, Input, InputNumber, Modal, Select, Table, Tabs, Upload } from 'antd';
import { BankOutlined, DeleteOutlined, EyeOutlined, PaperClipOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { ExpensePaymentTag, useExpenseWorkspaceStyle } from '../ExpenseWorkspace';
import { formatExpenseAmount, getExpenseDocuments, getExpenseRemaining } from '../expenseWorkspaceUtils';
import ExpenseDocumentsPanel from './ExpenseDocumentsPanel';
import '../expense-modals.css';

function ExpenseModalTitle({ title, subtitle, establishment }) {
  return <div className="expense-modal__heading">
    <div><h2>{title}</h2><p>{subtitle}</p></div>
    {establishment && <span className="expense-modal__context"><BankOutlined aria-hidden="true" /> {establishment}</span>}
  </div>;
}

export function ExpenseFormModal({ open, form, expense, establishment, categories, suppliers,
  loadingDependencies, dependencyError, onRetryDependencies, onChoiceOpen, documents, uploading, maxDocuments = 1, documentOptionsLoading,
  documentOptionsError, onRetryDocumentOptions, saving, onSave, onCancel, onUpload, onRemove, onPreview }) {
  const style = useExpenseWorkspaceStyle();
  const amount = Form.useWatch('amount', form);
  const dueDate = Form.useWatch('due_date', form);
  const categoryId = Form.useWatch('expense_category_id', form);
  const busy = saving || uploading;
  return <Modal className="expense-modal expense-modal--form" open={open} width={1060}
    style={{ ...style, top: 24 }} destroyOnClose closable={!busy} keyboard={!busy} maskClosable={!busy}
    title={<ExpenseModalTitle title={expense ? 'Modifier la charge administrative' : 'Nouvelle charge administrative'}
      subtitle="Renseignez la dépense et joignez son justificatif." establishment={establishment} />}
    onCancel={onCancel} footer={<div className="expense-modal__footer">
      <span className="expense-modal__required">* Champs obligatoires</span>
      <div><Button onClick={onCancel} disabled={busy}>Annuler</Button>
        <Button type="primary" loading={saving} disabled={uploading || documentOptionsLoading || !!documentOptionsError} onClick={onSave}>
          {expense ? 'Mettre à jour la charge' : 'Enregistrer la charge'}
        </Button></div>
    </div>}>
    {dependencyError && <Alert type="warning" showIcon message={dependencyError}
      action={<Button onClick={onRetryDependencies} loading={loadingDependencies}>Réessayer les listes</Button>} />}
    <div className="expense-modal__layout">
      <Form form={form} layout="vertical" name="expense_form" disabled={saving}>
        <h3>Informations de la dépense</h3>
        <div className="expense-modal__fields">
          <Form.Item name="date" label="Date" rules={[{ required: true, message: 'Veuillez choisir la date !' }]}>
            <DatePicker format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="expense_category_id" label="Catégorie" rules={[{ required: true, message: 'Veuillez sélectionner une catégorie !' }]}>
            <Select placeholder="Sélectionner une catégorie" loading={loadingDependencies}
              onDropdownVisibleChange={onChoiceOpen}
              options={categories.map((category) => ({ value: category.id, label: category.name }))} />
          </Form.Item>
          <Form.Item name="amount" label="Montant" rules={[{ required: true, message: 'Veuillez saisir le montant !' },
            { type: 'number', min: 0.01, message: 'Le montant doit être positif.' }]}>
            <InputNumber precision={2} min={0.01} max={99999999999999.99} addonAfter="FCFA" />
          </Form.Item>
          <Form.Item name="supplier_id" label="Fournisseur (recommandé)"
            extra="Nécessaire pour pouvoir enregistrer un paiement sur cette dépense.">
            <Select placeholder="Sélectionner un fournisseur" allowClear loading={loadingDependencies}
              showSearch optionFilterProp="label" onDropdownVisibleChange={onChoiceOpen}
              options={suppliers.map((supplier) => ({ value: supplier.id, label: supplier.name }))} />
          </Form.Item>
          <Form.Item name="bill" label="Référence facture (optionnelle)"><Input /></Form.Item>
          <Form.Item name="due_date" label="Échéance de paiement" extra="Date prévue pour régler le solde."
            rules={[{ required: true, message: 'Veuillez choisir la date d’échéance.' }]}>
            <DatePicker format="DD/MM/YYYY" />
          </Form.Item>
        </div>
        <Form.Item name="notes" label="Notes (optionnelles)"><Input.TextArea rows={2} placeholder="Ajouter une précision…" /></Form.Item>
        <section className="expense-modal__attachments" aria-label="Pièces justificatives">
          <h3>Pièces justificatives <small>(fortement recommandées)</small></h3>
          {documentOptionsError && <Alert type="error" showIcon message={documentOptionsError}
            action={<Button onClick={onRetryDocumentOptions}>Réessayer</Button>} />}
          <div className="expense-modal__upload">
            <Upload beforeUpload={onUpload} accept=".pdf,.jpg,.jpeg,.png" multiple={maxDocuments > 1}
              showUploadList={false} disabled={busy || documentOptionsLoading || !!documentOptionsError || documents.length >= maxDocuments}>
              <Button icon={<PaperClipOutlined />} loading={uploading}
                disabled={busy || documentOptionsLoading || !!documentOptionsError || documents.length >= maxDocuments}>
                Joindre un justificatif
              </Button>
            </Upload>
            <span>PDF, JPEG ou PNG · 10 Mo maximum par fichier · {maxDocuments} document{maxDocuments > 1 ? 's' : ''} maximum</span>
          </div>
          {documents.length > 0 && <ul className="expense-modal__file-list">{documents.map((document, index) =>
            <li key={document.key || document.url}>
              <Button type="link" icon={<EyeOutlined />} onClick={() => onPreview(document, index)}>{document.name}</Button>
              <Button type="text" icon={<DeleteOutlined />} disabled={busy} aria-label={`Retirer ${document.name}`} onClick={() => onRemove(document)}>Retirer</Button>
            </li>)}</ul>}
          <p className="expense-modal__help">Enregistrement possible sans document.</p>
        </section>
      </Form>
      <aside className="expense-modal__summary" aria-label="Résumé de la charge">
        <h3>Résumé de la charge</h3>
        <div className="expense-modal__amount">{amount === undefined || amount === null ? 'À renseigner' : formatExpenseAmount(amount)}</div>
        <p className="expense-modal__help">Montant à enregistrer</p>
        <dl><div><dt>Établissement</dt><dd>{establishment || 'Établissement sélectionné'}</dd></div>
          <div><dt>Échéance</dt><dd>{dueDate ? dayjs(dueDate).format('DD/MM/YYYY') : 'À renseigner'}</dd></div></dl>
        {!categoryId && <Alert type="warning" showIcon message="Catégorie à sélectionner" />}
        <p className="expense-modal__document-status"><PaperClipOutlined aria-hidden="true" />
          {documents.length ? `${documents.length} justificatif${documents.length > 1 ? 's' : ''} joint${documents.length > 1 ? 's' : ''}` : 'Aucun justificatif joint'}</p>
      </aside>
    </div>
  </Modal>;
}

function ExpensePaymentSummary({ expense, canAddPayment, paymentDisabledReason, onPayment }) {
  return <aside className="expense-modal__summary" aria-label="Situation du paiement">
    <h3>Situation du paiement</h3>
    <ExpensePaymentTag expense={expense} />
    <p className="expense-modal__balance-label">Solde à régler</p>
    <div className="expense-modal__amount">{formatExpenseAmount(getExpenseRemaining(expense))}</div>
    <dl><div><dt>Montant de la charge</dt><dd>{formatExpenseAmount(expense.amount)}</dd></div>
      <div><dt>Montant payé</dt><dd>{formatExpenseAmount(expense.paid_amount)}</dd></div></dl>
    <Button type="primary" block icon={<PlusOutlined aria-hidden="true" />} disabled={!canAddPayment} onClick={onPayment}>Ajouter un paiement</Button>
    {!canAddPayment && <p className="expense-modal__help">{paymentDisabledReason}</p>}
  </aside>;
}

export function ExpenseDetailsModal({ open, expense, companyId, moduleScope, payments, paymentColumns,
  paymentsLoading, canAddPayment, paymentDisabledReason, onPayment, onClose }) {
  const style = useExpenseWorkspaceStyle();
  const [tab, setTab] = useState('info');
  if (!expense) return null;
  const documents = getExpenseDocuments(expense);
  const layout = (content) => <div className="expense-modal__layout">{content}
    <ExpensePaymentSummary expense={expense} canAddPayment={canAddPayment} paymentDisabledReason={paymentDisabledReason} onPayment={onPayment} />
  </div>;
  const info = <section aria-label="Informations de la dépense">
    <h3>Informations de la dépense</h3>
    <dl className="expense-modal__information">
      <div><dt>Date</dt><dd>{expense.date ? dayjs(expense.date).format('DD/MM/YYYY HH:mm') : 'Non renseignée'}</dd></div>
      <div><dt>Échéance de paiement</dt><dd>{expense.due_date ? dayjs(expense.due_date).format('DD/MM/YYYY') : 'Non renseignée'}</dd></div>
      <div><dt>Établissement</dt><dd>{expense.warehouse_name || 'Non renseigné'}</dd></div>
      <div><dt>Catégorie</dt><dd>{expense.category_name || 'Non renseignée'}</dd></div>
      <div><dt>Fournisseur</dt><dd>{expense.supplier_name || 'Non renseigné'}</dd></div>
      <div><dt>Référence facture</dt><dd>{expense.bill || 'Non renseignée'}</dd></div>
      <div className="expense-modal__full-field"><dt>Notes</dt><dd>{expense.notes || <span className="expense-modal__help">Aucune note</span>}</dd></div>
      <div className="expense-modal__full-field"><dt>Pièces justificatives</dt><dd>{documents.length
        ? <Button type="link" icon={<PaperClipOutlined />} onClick={() => setTab('documents')}>Voir les documents ({documents.length})</Button>
        : <span className="expense-modal__help"><PaperClipOutlined aria-hidden="true" /> Aucun document joint</span>}</dd></div>
      <div className="expense-modal__full-field"><dt>Enregistré par</dt><dd>{expense.user_name || 'Non renseigné'}</dd></div>
    </dl>
  </section>;
  return <Modal className="expense-modal expense-modal--details" open={open} width={1060} style={{ ...style, top: 24 }} destroyOnClose
    title={<ExpenseModalTitle title="Détail de la charge administrative" subtitle="Consultez la dépense et suivez son règlement." establishment={expense.warehouse_name} />}
    onCancel={onClose} footer={<Button onClick={onClose}>Fermer</Button>}>
    <Tabs activeKey={tab} onChange={setTab} destroyInactiveTabPane animated={{ tabPane: false }} items={[
      { key: 'info', label: 'Informations générales', children: layout(info) },
      { key: 'payments', label: 'Paiements', children: layout(<section aria-label="Historique des paiements">
        <h3>Historique des paiements</h3><Table columns={paymentColumns} dataSource={payments} loading={paymentsLoading} pagination={false}
          size="small" scroll={{ x: 800 }} rowKey={(record) => String(record.payment_id || record.expense_payment_id || record.id)} />
      </section>) },
      { key: 'documents', label: 'Document', children: layout(<ExpenseDocumentsPanel expense={expense} documents={documents} companyId={companyId} moduleScope={moduleScope} />) },
    ]} />
  </Modal>;
}
